import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';
import { ModalService } from 'ui-components/services/modal-service';

import { ErrorModal } from '../../components/modals/error-modal';
import { LogService } from '../../services/log-service';

export const messages = defineMessages({
  header: {
    description: 'header for the error modal shown via the error boundary',
    defaultMessage: 'Please try again.',
    id: 'TyRY66',
  },
  callToAction: {
    description: 'Call to action for the user after an error',
    defaultMessage: 'If the issue persists, please contact our support team.',
    id: 'rJh6Cx',
  },
  orderDetails: {
    description: 'errorBoundary.orderDetails',
    defaultMessage: 'There was a problem loading this Order.',
    id: 'Udb8Zq',
  },
  shipmentDetails: {
    description: 'errorBoundary.shipmentDetails',
    defaultMessage: 'There was a problem loading this Shipment.',
    id: '8M0wc5',
  },
  shipmentsGrid: {
    description: 'errorBoundary.shipmentsGrid',
    defaultMessage: 'There was a problem loading your Shipments.',
    id: 'MD6gp/',
  },
  ordersGrid: {
    description: 'errorBoundary.ordersGrid',
    defaultMessage: 'There was a problem loading your Orders.',
    id: '/pmVIF',
  },
  productsGrid: {
    description: 'errorBoundary.productsGrid',
    defaultMessage: 'There was a problem loading your Products.',
    id: 'hIm7tY',
  },
  purchaseOrdersGrid: {
    description: 'A message to show the user when the error boundary is triggered in the purchase orders grid',
    defaultMessage: 'There was a problem loading your Purchase Orders.',
    id: 'e/hpRM',
  },
  transferOrdersGrid: {
    description: 'A message to show the user when the error boundary is triggered in the transfer orders grid',
    defaultMessage: 'There was a problem loading your Transfer Orders.',
    id: 'rA0pdn',
  },
  customersGrid: {
    description: 'errorBoundary.customersGrid',
    defaultMessage: 'There was a problem loading Customers data.',
    id: 'C2Go76',
  },
  app: {
    description: 'errorBoundary.app',
    defaultMessage: 'There was a problem loading ShipStation.',
    id: 'vgdjc/',
  },
  drawer: {
    description: 'errorBoundary.drawer',
    defaultMessage: 'There was a problem loading the Sidebar.',
    id: 'i7hYnb',
  },
  multiPackageModal: {
    description: 'errorBoundary.multiPackageModal',
    defaultMessage: 'There was a problem loading this package set.',
    id: 'ZgJDeg',
  },
  returnShipment: {
    description: 'errorBoundary.returnShipment',
    defaultMessage: 'There was a problem loading this Return.',
    id: 'xHHJ4X',
  },
  sidebarCswExternal: {
    description: 'errorBoundary.sidebarCswExternal',
    defaultMessage: 'There was a problem loading the Sidebar.',
    id: 'nWjhsu',
  },
  sidebarCswShipment: {
    description: 'errorBoundary.sidebarCswShipment',
    defaultMessage: 'There was a problem loading the Sidebar.',
    id: 'sSBzIe',
  },
  orderDetailsCswExternal: {
    description: 'errorBoundary.orderDetailsCswExternal',
    defaultMessage: 'There was a problem loading Order Details.',
    id: '7Xxk3u',
  },
  orderDetailsCswShipment: {
    description: 'errorBoundary.orderDetailsCswShipment',
    defaultMessage: 'There was a problem loading Order Details',
    id: 'wDz4Kk',
  },
  rateCalcCswExternal: {
    description: 'errorBoundary.rateCalcCswExternal',
    defaultMessage: 'There was a problem loading the Rate Calculator.',
    id: 'ookenK',
  },
  rateCalcCswShipment: {
    description: 'errorBoundary.rateCalcCswShipment',
    defaultMessage: 'There was a problem loading the Rate Calculator.',
    id: '9VC6oC',
  },
  scheduledPickups: {
    description: 'errorBoundary.scheduledPickups',
    defaultMessage: 'There was a problem loading your Scheduled Pickups',
    id: 'Jq4yEb',
  },
  scanTo: {
    description: 'errorBoundary.scanTo',
    defaultMessage: 'There was a problem loading this order.',
    id: 'FnB1Zk',
  },
  customsWidget: {
    description: 'errorBoundary.customsWidget',
    defaultMessage: 'There was a problem loading customs.',
    id: 'OT1uuu',
  },
  markAsShippedModal: {
    description: 'errorBoundary.markAsShippedModal',
    defaultMessage: 'There was a problem loading the Mark As Shipped Modal.',
    id: 'gjP+3p',
  },
  exportRecordsModal: {
    description: 'errorBoundary.exportRecordsModal',
    defaultMessage: 'There was a problem loading Exports',
    id: 'GLcGeL',
  },
  eodClosedShipmentsFormsGrid: {
    description: 'errorBoundary.eodClosedShipmentsFormsGrid',
    defaultMessage: 'There was a problem loading your Shipments.',
    id: 'EAaCNA',
  },
  eodCloseShipmentsModal: {
    description: 'errorBoundary.eodCloseShipmentsModal',
    defaultMessage: 'There was a problem loading your Shipments.',
    id: 'iYe1Q1',
  },
  walletRegistrationModal: {
    description: 'Wallet registration modal boundary',
    defaultMessage: 'There was an issue loading the registration information.',
    id: 'o0NoCA',
  },
  deleteCollectionModal: {
    description: 'Error deleting a collection',
    defaultMessage: 'There was an issue deleting this collection.',
    id: 'qyDsXe',
  },
  checkoutRatesPreview: {
    description: 'Error fetching checkout rates preview',
    defaultMessage: 'There was an issue fetching the Checkout Rates preview.',
    id: 'OV2hQM',
  },
  returnsGrid: {
    description: 'errorBoundary.returnsGrid',
    defaultMessage: 'There was a problem loading your Returns.',
    id: 'kzoJie',
  },
  batchesGrid: {
    description: 'A message to show the user when the application encounters an error in rendering the batches page',
    defaultMessage: 'There was an issue loading this batch.',
    id: 'zq7On5',
  },
  walletUpdatePaymentError: {
    description: 'Error message for wallet update payment',
    defaultMessage: 'There was an issue while trying to update your payment method.',
    id: 'lu7qvI',
  },
  inventoryGrid: {
    description: 'A message to show the user when the application encounters an error in rendering the inventory data',
    defaultMessage: 'There was an issue loading inventory data.',
    id: '44Gfyv',
  },
  paymentsTableError: {
    description: 'Error message for payments table',
    defaultMessage: 'There was an issue loading your payment methods.',
    id: '37PMnN',
  },
  advancedShippingNoticeGrid: {
    description: 'A message to show the user when the application encounters an error in rendering the advance shipping notice grid',
    defaultMessage: 'There was an issue loading this grid. Please refresh or try again later.',
    id: 'rNDBKt',
  },
  fulfillmentsGrid: {
    description: 'A message to show the user when the application encounters an error in rendering the fulfillments grid',
    defaultMessage: 'There was an issue loading this grid. Please refresh or try again later.',
    id: 'BMJyed',
  },
  rateShopperPage: {
    description: 'A message to show the user when the application encounters an error in rendering the rate shopper page',
    defaultMessage: 'There was an issue loading this page. Please refresh or try again later.',
    id: 'mg+r3J',
  },
  homePage2: {
    description: 'A message to show the user when the application encounters an error in rendering home page 2',
    defaultMessage: 'There was a problem loading the Home page.',
    id: 'LlzwAH',
  },
  speedShipRegistrationModal: {
    description: 'A message to show the user when the application encounters an error in the WWEX registration modal',
    defaultMessage: 'There was a problem loading the WWEX registration form.',
    id: 'jeseLE',
  },
});
const messageKeys = Object.keys(messages);

class ErrorBoundary extends React.Component {
  static propTypes = {
    errorMessageId: PropTypes.oneOf(messageKeys).isRequired,
    children: PropTypes.node.isRequired,
    onError: PropTypes.func,
  };

  static defaultProps = {
    onError: noop,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const { errorMessageId, onError } = this.props;
    if (error) {
      LogService.logError(error, info);

      const errorModalMessage = messages[errorMessageId];

      ModalService.closeLastOpenModal();
      ModalService.launchModal({
        component: ErrorModal,
        props: {
          title: <FormattedMessage {...messages.header} />,
          message: (
            <>
              <div>{errorModalMessage ? <FormattedMessage {...errorModalMessage} /> : null}</div>
              <div>
                <FormattedMessage {...messages.callToAction} />
              </div>
            </>
          ),
        },
      });
      onError(error);
    }
  }

  render() {
    if (this.state?.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export { ErrorBoundary };
export default ErrorBoundary;
